import { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
  where,
  limit,
} from "firebase/firestore";
import { db } from "../firebase";

/* ---------------- utils ---------------- */

function fmt(ts) {
  if (!ts) return "-";
  const d = new Date(ts);
  return d.toLocaleString("es-AR");
}

function includesText(haystack, needle) {
  const h = String(haystack || "").toLowerCase();
  const n = String(needle || "").toLowerCase().trim();
  if (!n) return true;
  return h.includes(n);
}

function domainFromUrl(url) {
  const u = String(url || "").trim();
  if (!u) return "Sin dominio";
  return u.replace(/^https?:\/\//, "").replace(/\/$/, "");
}

function shortLabel(s) {
  return String(s?.listName || s?.originUrl || s?.id || "Lista");
}

function groupByDomain(stateKeys) {
  const map = new Map();
  for (const s of stateKeys) {
    const dom = domainFromUrl(s?.originUrl);
    if (!map.has(dom)) map.set(dom, []);
    map.get(dom).push(s);
  }

  const groups = Array.from(map.entries()).map(([domain, items]) => {
    items.sort((a, b) =>
      String(a.listName || "").localeCompare(String(b.listName || ""))
    );
    return { domain, items };
  });

  groups.sort((a, b) => a.domain.localeCompare(b.domain));
  return groups;
}

const TABS = [
  { id: "stock", label: "Stock actual" },
  { id: "out", label: "Sin stock" },
  { id: "new", label: "Agregados" },
  { id: "back", label: "Volvieron" },
  { id: "config", label: "Config" },
];

/* ---------------- main ---------------- */

export default function RobotStockDashboard() {
  const [stateKeys, setStateKeys] = useState([]);
  const [selectedStateKey, setSelectedStateKey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rootError, setRootError] = useState(null);

  const [tab, setTab] = useState("stock");
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    const rootCol = collection(db, "robotCheckStockProducts");

    const unsub = onSnapshot(
      rootCol,
      (snap) => {
        setLoading(false);
        setRootError(null);

        const keys = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        keys.sort((a, b) =>
          String(a.originUrl || "").localeCompare(String(b.originUrl || ""))
        );

        setStateKeys(keys);

        if (!selectedStateKey && keys.length > 0) {
          setSelectedStateKey(keys[0].id);
        }
      },
      (err) => {
        setLoading(false);
        setRootError(err);
        console.error("Firestore error robotCheckStockProducts:", err);
      }
    );

    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSearchText("");
  }, [tab, selectedStateKey]);

  if (loading) {
    return (
      <Page>
        <Header title="Robot Check Stock" subtitle="Cargando…" />
        <Skeleton />
      </Page>
    );
  }

  if (rootError) {
    return (
      <Page>
        <Header title="Robot Check Stock" subtitle="Error" />
        <Card>
          <div style={{ color: "crimson", fontWeight: 700 }}>
            Error leyendo Firestore
          </div>
          <div style={{ opacity: 0.8, marginTop: 6 }}>{rootError.message}</div>
          <div style={{ opacity: 0.65, marginTop: 10, fontSize: 13 }}>
            Probables causas: reglas Firestore, proyecto equivocado, o db mal inicializado.
          </div>
        </Card>
      </Page>
    );
  }

  if (!stateKeys.length) {
    return (
      <Page>
        <Header title="Robot Check Stock" subtitle="Sin datos" />
        <Empty
          title="Todavía no hay listas"
          description={
            <>
              La colección <b>robotCheckStockProducts</b> está vacía. <br />
              Mandá un webhook válido y se va a llenar automáticamente.
            </>
          }
        />
      </Page>
    );
  }

  if (!selectedStateKey) {
    return (
      <Page>
        <Header title="Robot Check Stock" subtitle="Seleccionando lista…" />
        <Skeleton />
      </Page>
    );
  }

  const selectedMeta = stateKeys.find((s) => s.id === selectedStateKey) || null;

  return (
    <Page>
      <div style={{ display: "grid", gap: 10 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            alignItems: "flex-end",
          }}
        >
          <div style={{ display: "grid", gap: 4 }}>
            <div style={styles.h1}>Robot Check Stock</div>
            <div style={styles.sub}>
              {selectedMeta?.originUrl ? (
                <>
                  <span style={styles.dot} />
                  <span>{selectedMeta.originUrl}</span>
                  <span style={styles.dot} />
                  <span>{selectedMeta.listName || "Lista"}</span>
                </>
              ) : (
                <span style={{ opacity: 0.7 }}>Seleccioná una lista</span>
              )}
            </div>
          </div>

          <div style={{ fontSize: 12, opacity: 0.65, textAlign: "right" }}>
            stateKey: {selectedStateKey}
          </div>
        </div>

        {/* ✅ quick buttons agrupados por dominio */}
        <QuickGroups
          stateKeys={stateKeys}
          selectedStateKey={selectedStateKey}
          onSelect={setSelectedStateKey}
        />

        {/* select fallback */}
        <select
          value={selectedStateKey}
          onChange={(e) => setSelectedStateKey(e.target.value)}
          style={styles.select}
        >
          {stateKeys.map((s) => (
            <option key={s.id} value={s.id}>
              {domainFromUrl(s.originUrl)} — {shortLabel(s)}
            </option>
          ))}
        </select>
      </div>

      <Tabs tab={tab} setTab={setTab} />

      <Toolbar tab={tab} searchText={searchText} setSearchText={setSearchText} />

      <RobotStockDashboardByStateKey
        stateKey={selectedStateKey}
        tab={tab}
        searchText={searchText}
      />
    </Page>
  );
}

/* ==============================
   Quick groups
============================== */

function QuickGroups({ stateKeys, selectedStateKey, onSelect }) {
  const groups = useMemo(() => groupByDomain(stateKeys), [stateKeys]);

  return (
    <div style={{ display: "grid", gap: 10 }}>
      {groups.map((g) => (
        <div key={g.domain} style={stylesQuick.groupWrap}>
          <div style={stylesQuick.groupTitle}>{g.domain}</div>

          <div style={stylesQuick.quickRow}>
            {g.items.map((s) => {
              const active = s.id === selectedStateKey;
              return (
                <button
                  key={s.id}
                  onClick={() => onSelect(s.id)}
                  style={{
                    ...stylesQuick.chip,
                    ...(active ? stylesQuick.chipActive : {}),
                  }}
                  type="button"
                  title={`${g.domain} — ${shortLabel(s)}`}
                >
                  {shortLabel(s)}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ==============================
   Inner by stateKey
============================== */

function RobotStockDashboardByStateKey({ stateKey, tab, searchText }) {
  const [activeItems, setActiveItems] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [mutedKeys, setMutedKeys] = useState(new Set());

  const [eventsOut, setEventsOut] = useState([]);
  const [eventsNew, setEventsNew] = useState([]);
  const [eventsBack, setEventsBack] = useState([]);

  // Config filters
  const [onlyMuted, setOnlyMuted] = useState(false);
  const [onlyActive, setOnlyActive] = useState(false);
  const [onlyInactive, setOnlyInactive] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  useEffect(() => {
    setOnlyMuted(false);
    setOnlyActive(false);
    setOnlyInactive(false);
    setBulkBusy(false);
  }, [stateKey]);

  useEffect(() => {
    if (onlyActive && onlyInactive) setOnlyInactive(false);
  }, [onlyActive, onlyInactive]);

  const itemsCol = useMemo(
    () => collection(db, "robotCheckStockProducts", stateKey, "items"),
    [stateKey]
  );
  const eventsCol = useMemo(() => collection(db, "robotCheckStockEvents"), []);

  // Activos no muteados
  useEffect(() => {
    const q1 = query(
      itemsCol,
      where("active", "==", true),
      where("muted", "==", false)
    );
    return onSnapshot(q1, (snap) => {
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      rows.sort((a, b) =>
        String(a.name || "").localeCompare(String(b.name || ""))
      );
      setActiveItems(rows);
    });
  }, [itemsCol]);

  // Todos + mutedKeys
  useEffect(() => {
    return onSnapshot(itemsCol, (snap) => {
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      rows.sort((a, b) =>
        String(a.name || "").localeCompare(String(b.name || ""))
      );
      setAllItems(rows);

      const muted = new Set(rows.filter((r) => r.muted).map((r) => r.id));
      setMutedKeys(muted);
    });
  }, [itemsCol]);

  // Eventos
  useEffect(() => {
    const qOut = query(
      eventsCol,
      where("stateKey", "==", stateKey),
      where("type", "==", "OUT_OF_STOCK"),
      orderBy("occurredAt", "desc"),
      limit(250)
    );
    return onSnapshot(qOut, (snap) => {
      setEventsOut(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
  }, [eventsCol, stateKey]);

  useEffect(() => {
    const qNew = query(
      eventsCol,
      where("stateKey", "==", stateKey),
      where("type", "==", "NEW"),
      orderBy("occurredAt", "desc"),
      limit(250)
    );
    return onSnapshot(qNew, (snap) => {
      setEventsNew(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
  }, [eventsCol, stateKey]);

  useEffect(() => {
    const qBack = query(
      eventsCol,
      where("stateKey", "==", stateKey),
      where("type", "==", "BACK_IN_STOCK"),
      orderBy("occurredAt", "desc"),
      limit(250)
    );
    return onSnapshot(qBack, (snap) => {
      setEventsBack(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
  }, [eventsCol, stateKey]);

  const filterMutedEvents = (events) =>
    events.filter((e) => !mutedKeys.has(e.productKey));

  const filteredStock = activeItems.filter((p) =>
    includesText(p.name || p.id, searchText)
  );

  const filteredOut = filterMutedEvents(eventsOut).filter((e) =>
    includesText(e.name || e.productKey, searchText)
  );
  const filteredNew = filterMutedEvents(eventsNew).filter((e) =>
    includesText(e.name || e.productKey, searchText)
  );
  const filteredBack = filterMutedEvents(eventsBack).filter((e) =>
    includesText(e.name || e.productKey, searchText)
  );

  const filteredConfig = useMemo(() => {
    let rows = allItems;

    rows = rows.filter((p) => includesText(p.name || p.id, searchText));
    if (onlyMuted) rows = rows.filter((p) => p.muted === true);
    if (onlyActive) rows = rows.filter((p) => p.active === true);
    if (onlyInactive) rows = rows.filter((p) => p.active !== true);

    return rows;
  }, [allItems, searchText, onlyMuted, onlyActive, onlyInactive]);

  const mutedCount = allItems.filter((x) => x.muted).length;

  const lastUpdated = useMemo(() => {
    let max = 0;
    for (const p of allItems) max = Math.max(max, Number(p.lastSeenAt || 0));
    return max || null;
  }, [allItems]);

  async function toggleMute(productKey, currentMuted) {
    const ref = doc(db, "robotCheckStockProducts", stateKey, "items", productKey);
    await updateDoc(ref, { muted: !currentMuted, mutedAt: Date.now() });
  }

  async function unmuteFiltered() {
    if (bulkBusy) return;
    setBulkBusy(true);
    try {
      const toUnmute = filteredConfig.filter((p) => p.muted === true);
      for (const p of toUnmute) {
        const ref = doc(db, "robotCheckStockProducts", stateKey, "items", p.id);
        // eslint-disable-next-line no-await-in-loop
        await updateDoc(ref, { muted: false, mutedAt: Date.now() });
      }
    } finally {
      setBulkBusy(false);
    }
  }

  return (
    <>
      <KpiGrid>
        <Kpi title="Activos" value={activeItems.length} />
        <Kpi title="Agregados (hist)" value={filterMutedEvents(eventsNew).length} />
        <Kpi title="Sin stock (hist)" value={filterMutedEvents(eventsOut).length} />
        <Kpi title="Volvieron (hist)" value={filterMutedEvents(eventsBack).length} />
        <Kpi title="Silenciados" value={mutedCount} />
      </KpiGrid>

      <Card>
        <div style={styles.metaRow}>
          <div style={{ display: "grid", gap: 2 }}>
            <div style={{ fontWeight: 800 }}>Estado</div>
            <div style={{ fontSize: 13, opacity: 0.7 }}>
              Última actualización: <b>{lastUpdated ? fmt(lastUpdated) : "-"}</b>
            </div>
          </div>

          <Chip>
            {tab === "stock" && `${filteredStock.length} items`}
            {tab === "out" && `${filteredOut.length} eventos`}
            {tab === "new" && `${filteredNew.length} eventos`}
            {tab === "back" && `${filteredBack.length} eventos`}
            {tab === "config" && `${filteredConfig.length} items`}
          </Chip>
        </div>
      </Card>

      {tab === "stock" && (
        <Card>
          <CardTitle title="✅ Productos actuales (en lista)" />
          {filteredStock.length === 0 ? (
            <EmptySmall text="No hay productos activos con ese filtro." />
          ) : (
            <Table>
              <thead>
                <tr>
                  <th>Producto</th>
                  <th style={{ width: 220 }}>Última vez visto</th>
                </tr>
              </thead>
              <tbody>
                {filteredStock.map((p) => (
                  <tr key={p.id}>
                    <td style={{ fontWeight: 600 }}>{p.name || p.id}</td>
                    <td style={{ opacity: 0.8 }}>{fmt(p.lastSeenAt)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      )}

      {tab === "out" && (
        <Card>
          <CardTitle title="🚫 Se quedaron sin stock" />
          {filteredOut.length === 0 ? (
            <EmptySmall text="No hay eventos de sin stock con ese filtro." />
          ) : (
            <Table>
              <thead>
                <tr>
                  <th>Producto</th>
                  <th style={{ width: 240 }}>Fecha</th>
                </tr>
              </thead>
              <tbody>
                {filteredOut.map((e) => (
                  <tr key={e.id}>
                    <td style={{ fontWeight: 600 }}>{e.name || e.productKey}</td>
                    <td style={{ opacity: 0.85 }}>{fmt(e.occurredAt)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      )}

      {tab === "new" && (
        <Card>
          <CardTitle title="🆕 Agregados recientemente" />
          {filteredNew.length === 0 ? (
            <EmptySmall text="No hay eventos de nuevos productos con ese filtro." />
          ) : (
            <Table>
              <thead>
                <tr>
                  <th>Producto</th>
                  <th style={{ width: 240 }}>Fecha</th>
                </tr>
              </thead>
              <tbody>
                {filteredNew.map((e) => (
                  <tr key={e.id}>
                    <td style={{ fontWeight: 600 }}>{e.name || e.productKey}</td>
                    <td style={{ opacity: 0.85 }}>{fmt(e.occurredAt)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      )}

      {tab === "back" && (
        <Card>
          <CardTitle title="🔁 Volvieron a aparecer" />
          {filteredBack.length === 0 ? (
            <EmptySmall text="No hay eventos de 'volvió' con ese filtro." />
          ) : (
            <Table>
              <thead>
                <tr>
                  <th>Producto</th>
                  <th style={{ width: 240 }}>Fecha</th>
                </tr>
              </thead>
              <tbody>
                {filteredBack.map((e) => (
                  <tr key={e.id}>
                    <td style={{ fontWeight: 600 }}>{e.name || e.productKey}</td>
                    <td style={{ opacity: 0.85 }}>{fmt(e.occurredAt)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      )}

      {tab === "config" && (
        <Card>
          <CardTitle
            title="⚙️ Configuración (silenciar productos)"
            subtitle="Los silenciados no aparecen en el dashboard."
          />

          <div style={styles.configFilters}>
            <Toggle
              label="Solo silenciados"
              checked={onlyMuted}
              onChange={() => setOnlyMuted((v) => !v)}
            />
            <Toggle
              label="Solo activos"
              checked={onlyActive}
              onChange={() => {
                setOnlyActive((v) => !v);
                if (!onlyActive) setOnlyInactive(false);
              }}
            />
            <Toggle
              label="Solo inactivos"
              checked={onlyInactive}
              onChange={() => {
                setOnlyInactive((v) => !v);
                if (!onlyInactive) setOnlyActive(false);
              }}
            />

            <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
              <Button
                tone="ghost"
                onClick={() => {
                  setOnlyMuted(false);
                  setOnlyActive(false);
                  setOnlyInactive(false);
                }}
              >
                Limpiar filtros
              </Button>

              <Button
                tone="ghost"
                disabled={
                  bulkBusy || filteredConfig.filter((p) => p.muted).length === 0
                }
                onClick={unmuteFiltered}
              >
                {bulkBusy ? "Procesando…" : "Des-silenciar filtrados"}
              </Button>
            </div>
          </div>

          {filteredConfig.length === 0 ? (
            <EmptySmall text="No hay productos con ese filtro." />
          ) : (
            <Table>
              <thead>
                <tr>
                  <th>Producto</th>
                  <th style={{ width: 90, textAlign: "center" }}>Activo</th>
                  <th style={{ width: 100, textAlign: "center" }}>Muted</th>
                  <th style={{ width: 220 }}>Última vez visto</th>
                  <th style={{ width: 180, textAlign: "right" }}>Acción</th>
                </tr>
              </thead>
              <tbody>
                {filteredConfig.map((p) => (
                  <tr key={p.id}>
                    <td style={{ fontWeight: 650 }}>{p.name || p.id}</td>
                    <td style={{ textAlign: "center", opacity: 0.85 }}>
                      {p.active ? "Sí" : "No"}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <Pill tone={p.muted ? "muted" : "ok"}>
                        {p.muted ? "Sí" : "No"}
                      </Pill>
                    </td>
                    <td style={{ opacity: 0.85 }}>{fmt(p.lastSeenAt)}</td>
                    <td style={{ textAlign: "right" }}>
                      <Button
                        tone={p.muted ? "ghost" : "danger"}
                        onClick={() => toggleMute(p.id, !!p.muted)}
                      >
                        {p.muted ? "Des-silenciar" : "Silenciar"}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      )}
    </>
  );
}

/* ==============================
   UI components (sin libs)
============================== */

function Page({ children }) {
  return <div style={styles.page}>{children}</div>;
}

function Tabs({ tab, setTab }) {
  return (
    <div style={styles.tabs}>
      {TABS.map((t) => (
        <button
          key={t.id}
          onClick={() => setTab(t.id)}
          style={{
            ...styles.tab,
            ...(tab === t.id ? styles.tabActive : {}),
          }}
          type="button"
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

function Toolbar({ tab, searchText, setSearchText }) {
  const placeholder =
    tab === "config"
      ? "Buscar producto para silenciar…"
      : "Buscar producto…";

  return (
    <Card>
      <div style={styles.toolbar}>
        <div style={styles.searchWrap}>
          <span style={styles.searchIcon}>⌕</span>
          <input
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder={placeholder}
            style={styles.search}
          />
        </div>
        <div style={{ fontSize: 12, opacity: 0.7 }}>
          Tip: escribí parte del nombre.
        </div>
      </div>
    </Card>
  );
}

function Toggle({ label, checked, onChange }) {
  return (
    <button
      onClick={onChange}
      style={{
        ...styles.toggle,
        ...(checked ? styles.toggleOn : {}),
      }}
      type="button"
    >
      <span style={{ fontWeight: 900 }}>{checked ? "✓" : " "}</span>
      <span>{label}</span>
    </button>
  );
}

function Card({ children }) {
  return <div style={styles.card}>{children}</div>;
}

function CardTitle({ title, subtitle }) {
  return (
    <div style={{ display: "grid", gap: 4, marginBottom: 12 }}>
      <div style={{ fontWeight: 900, fontSize: 16 }}>{title}</div>
      {subtitle ? (
        <div style={{ opacity: 0.7, fontSize: 13 }}>{subtitle}</div>
      ) : null}
    </div>
  );
}

function Table({ children }) {
  return <table style={styles.table}>{children}</table>;
}

function KpiGrid({ children }) {
  return <div style={styles.kpiGrid}>{children}</div>;
}

function Kpi({ title, value }) {
  return (
    <div style={styles.kpiCard}>
      <div style={{ opacity: 0.7, fontSize: 12 }}>{title}</div>
      <div style={{ fontWeight: 900, fontSize: 22, marginTop: 4 }}>{value}</div>
    </div>
  );
}

function Chip({ children }) {
  return <div style={styles.chip}>{children}</div>;
}

function Pill({ children, tone = "ok" }) {
  const st =
    tone === "muted"
      ? {
          background: "rgba(220,38,38,0.10)",
          border: "1px solid rgba(220,38,38,0.25)",
          color: "rgba(220,38,38,1)",
        }
      : {
          background: "rgba(16,185,129,0.10)",
          border: "1px solid rgba(16,185,129,0.25)",
          color: "rgba(16,185,129,1)",
        };

  return <span style={{ ...styles.pill, ...st }}>{children}</span>;
}

function Button({ children, onClick, tone = "ghost", disabled }) {
  const st =
    tone === "danger"
      ? styles.btnDanger
      : tone === "ghost"
      ? styles.btnGhost
      : styles.btnGhost;

  return (
    <button
      onClick={onClick}
      style={{
        ...styles.btn,
        ...st,
        ...(disabled ? styles.btnDisabled : {}),
      }}
      disabled={disabled}
      type="button"
    >
      {children}
    </button>
  );
}

function Header({ title, subtitle }) {
  return (
    <div style={{ padding: 16 }}>
      <div style={styles.h1}>{title}</div>
      <div style={styles.sub}>{subtitle}</div>
    </div>
  );
}

function Empty({ title, description }) {
  return (
    <Card>
      <div style={{ fontWeight: 900, fontSize: 16 }}>{title}</div>
      <div style={{ opacity: 0.75, marginTop: 6, lineHeight: 1.5 }}>
        {description}
      </div>
    </Card>
  );
}

function EmptySmall({ text }) {
  return <div style={{ opacity: 0.7, padding: "12px 0" }}>{text}</div>;
}

function Skeleton() {
  return (
    <div style={{ display: "grid", gap: 12, padding: 16 }}>
      <div style={styles.skel} />
      <div style={styles.skel} />
      <div style={styles.skel} />
    </div>
  );
}

/* ---------------- styles ---------------- */

const styles = {
  page: {
    padding: 16,
    maxWidth: 1100,
    margin: "0 auto",
    display: "grid",
    gap: 14,
    fontFamily:
      'ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, "Helvetica Neue", Arial',
  },

  h1: { fontWeight: 950, fontSize: 22, letterSpacing: -0.3 },
  sub: {
    fontSize: 13,
    opacity: 0.7,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  dot: {
    width: 4,
    height: 4,
    borderRadius: 999,
    background: "rgba(0,0,0,0.35)",
  },

  select: {
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid rgba(0,0,0,0.12)",
    background: "white",
    fontSize: 13,
    outline: "none",
    width: "100%",
    maxWidth: 900,
  },

  tabs: { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" },
  tab: {
    borderRadius: 999,
    border: "1px solid rgba(0,0,0,0.12)",
    padding: "8px 12px",
    fontSize: 13,
    background: "white",
    cursor: "pointer",
    opacity: 0.85,
  },
  tabActive: {
    borderColor: "rgba(0,0,0,0.22)",
    opacity: 1,
    fontWeight: 800,
  },

  card: {
    border: "1px solid rgba(0,0,0,0.10)",
    borderRadius: 16,
    padding: 14,
    background: "white",
    boxShadow: "0 1px 0 rgba(0,0,0,0.04)",
  },

  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
  },

  searchWrap: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    border: "1px solid rgba(0,0,0,0.12)",
    borderRadius: 12,
    padding: "10px 12px",
    minWidth: 320,
    flex: "1 1 320px",
  },

  searchIcon: { opacity: 0.5, fontSize: 14 },
  search: { border: "none", outline: "none", width: "100%", fontSize: 13 },

  kpiGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
    gap: 10,
  },
  kpiCard: {
    border: "1px solid rgba(0,0,0,0.10)",
    borderRadius: 16,
    padding: 12,
    background: "white",
  },

  table: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
    fontSize: 13,
    overflow: "hidden",
  },

  metaRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "center",
  },

  chip: {
    padding: "7px 10px",
    borderRadius: 999,
    fontSize: 12,
    border: "1px solid rgba(0,0,0,0.12)",
    background: "rgba(0,0,0,0.02)",
    opacity: 0.9,
  },

  pill: {
    padding: "4px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 800,
    display: "inline-block",
  },

  btn: {
    borderRadius: 12,
    padding: "8px 10px",
    border: "1px solid rgba(0,0,0,0.12)",
    fontSize: 13,
    cursor: "pointer",
    background: "white",
  },
  btnGhost: { background: "white" },
  btnDanger: {
    background: "rgba(220,38,38,0.10)",
    border: "1px solid rgba(220,38,38,0.25)",
  },
  btnDisabled: { opacity: 0.5, cursor: "not-allowed" },

  skel: { height: 52, borderRadius: 16, background: "rgba(0,0,0,0.06)" },

  configFilters: {
    display: "flex",
    gap: 10,
    alignItems: "center",
    flexWrap: "wrap",
    padding: "10px 0 6px",
    borderBottom: "1px solid rgba(0,0,0,0.06)",
    marginBottom: 10,
  },

  toggle: {
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    padding: "8px 10px",
    borderRadius: 999,
    border: "1px solid rgba(0,0,0,0.12)",
    background: "white",
    cursor: "pointer",
    fontSize: 13,
    opacity: 0.9,
  },
  toggleOn: {
    borderColor: "rgba(0,0,0,0.25)",
    fontWeight: 800,
  },
};

const stylesQuick = {
  groupWrap: {
    border: "1px solid rgba(0,0,0,0.08)",
    borderRadius: 12,
    padding: 10,
    background: "rgba(0,0,0,0.02)",
  },
  groupTitle: {
    fontSize: 12,
    fontWeight: 800,
    opacity: 0.75,
    marginBottom: 8,
  },
  quickRow: {
    display: "flex",
    gap: 8,
    overflowX: "auto",
    paddingBottom: 4,
    WebkitOverflowScrolling: "touch",
  },
  chip: {
    whiteSpace: "nowrap",
    borderRadius: 999,
    border: "1px solid rgba(0,0,0,0.14)",
    background: "white",
    padding: "8px 10px",
    fontSize: 12,
    cursor: "pointer",
    opacity: 0.9,
  },
  chipActive: {
    borderColor: "rgba(0,0,0,0.30)",
    fontWeight: 900,
    opacity: 1,
  },
};

/* Inject table hover styles (no CSS file needed) */
(() => {
  if (typeof document === "undefined") return;
  const id = "robot-stock-dashboard-inline-style";
  if (document.getElementById(id)) return;
  const style = document.createElement("style");
  style.id = id;
  style.innerHTML = `
    table tr td, table tr th { padding: 10px 8px; }
    table thead th { text-align: left; font-size: 12px; opacity: 0.7; border-bottom: 1px solid rgba(0,0,0,0.08); }
    table tbody tr:hover { background: rgba(0,0,0,0.02); }
  `;
  document.head.appendChild(style);
})();
